import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { IServiceChangeRequest } from './../../../../core/models/service-change-request.model';
import { IServiceTemplate } from './../../../../core/models/service-template/service-template.model';
import {
  ExtractHttpErrorResponseCodeAndMessage,
  IHttpErrorResponseCodeAndMessage,
  IHttpSingleDataResponse,
  IUiAlertContent,
  TUiAlertTypes,
  ToastService,
} from '@irembo-andela/irembogov3-common';
import { ServiceChangeRequestService } from './../../../../core/services/service-change-request.service';
import { ServiceTemplateService } from './../../../../core/services/service-template.service';
import {
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import {
  IError,
  JsonEditorComponent,
  JsonEditorOptions,
} from '@maaxgr/ang-jsoneditor';
import { Subject, finalize, takeUntil, BehaviorSubject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { FormBuilderService } from './../../../../core/services/form-builder.service';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { IFieldError } from './../../../../core/models/service-change-request-config-steps-forms.model';
import { FormlyFieldArrayToIremboFormlyFieldsConverter } from './../../../../core/utils/irembo-formly-field-allow-pops.utils';
import { HttpErrorResponse } from '@angular/common/http';
import { ExtractHttpErrorFieldErrorsUtil } from './../../../../core/utils/http-field-error-extractor.utils';
import { EServiceChangeRequestSection } from './../../../../core/models/service-change-request-sections.enum';
import { EServiceChangeRequestToggleHeaders } from './../../../../core/models/service-change-request-toggle-header-section.enum';
import {
  DefaultEmptyFormData,
  IFormBuilderFieldsData,
} from './../../../../core/models/form-builder-fields-data.model';

@Component({
  selector: 'irembogov-configuration-officer-form',
  templateUrl: './configuration-officer-form.component.html',
  styleUrls: ['./configuration-officer-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [FormBuilderService],
})
export class ConfigurationOfficerFormComponent
  implements AfterViewInit, OnChanges, OnDestroy, OnInit
{
  @Input() changeRequest!: IServiceChangeRequest;
  @Input() changeRequestThirdPartyFormData!: Record<string, unknown>;
  @Input() isTemplateMode = false;
  @Input() template: IServiceTemplate | null = null;
  @Output() officerFormSubmitted: EventEmitter<boolean> = new EventEmitter();

  @Output() officerThirdPartyFormPersisted = new EventEmitter<
    Record<string, unknown>
  >();

  isLoading = false;
  inReviiewMode = false;
  showConfigFormErrorMessage = false;
  @ViewChild('errorMessageElement') errorMessageElement!: ElementRef;
  @ViewChild(JsonEditorComponent, { static: false })
  editor!: JsonEditorComponent;

  formAlertContent: IUiAlertContent = {
    title: '',
    message: '',
    type: 'warning',
  };
  formExistsForm: FormGroup = new FormGroup({
    formExists: new FormControl('', [Validators.required]),
  });
  jsonEditorValidationErrors: ValidationErrors[] = [];
  public editorOptions!: JsonEditorOptions;
  jsonEditorFormData: any = {};
  formlyFieldsSubjectControl$: Subject<void> = new Subject<void>();
  DefaultEmptyFormData = DefaultEmptyFormData;
  transitionForm: FormGroup = new FormGroup({
    startState: new FormControl('', [Validators.required]),
    event: new FormControl('', [Validators.required]),
  });
  transitionKeys: string[] = [];
  selectedTransitionKey: string | null = null;
  workflowSteps: Array<{ startState: string; event: string }> = [];
  availableStartStates: string[] = [];
  availableEvents: string[] = [];
  workflowDropdownsLoading = false;

  EServiceChangeRequestSection = EServiceChangeRequestSection;
  EServiceChangeRequestToggleHeaders = EServiceChangeRequestToggleHeaders;
  selectedToggleHeader: EServiceChangeRequestToggleHeaders =
    EServiceChangeRequestToggleHeaders.WIZARD;
  formChanges$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  autoSaveInProgress = false;
  lastAutoSaved: Date | null = null;
  autoSaveTimeout: any = null;
  private codeEditorSyncTimeout: any = null;
  private codeModeApplyTimeout: any = null;
  private syncingFromEditor = false;
  private loadingTransition = false;

  constructor(
    private serviceChangeRequestService: ServiceChangeRequestService,
    private serviceTemplateService: ServiceTemplateService,
    private toastService: ToastService,
    private formBuilderService: FormBuilderService
  ) {
    this.editorOptions = new JsonEditorOptions();
    this.editorOptions.mode = 'code';
    this.editorOptions.modes = ['code'];

    this.editorOptions.onValidate = (json: unknown) =>
      this.checkJsonFormDataIsNotEmpty(json as Record<string, unknown>);
  }

  private initialiseFromThirdPartyForm(
    thirdPartyForm: Record<string, unknown>,
    source: 'external' | 'editor' = 'external'
  ): void {
    const isSingleFieldsObject =
      !!(thirdPartyForm as any)?.fields &&
      Array.isArray((thirdPartyForm as any).fields);
    const hasForm = isSingleFieldsObject
      ? (thirdPartyForm as any).fields.length > 0
      : !!thirdPartyForm && Object.keys(thirdPartyForm).length > 0;

    if (!hasForm) {
      this.onFormExistenceChange('NO');
      this.jsonEditorFormData = {};
      this.transitionKeys = [];
      this.selectedTransitionKey = null;
      this.formBuilderService.loadJsonFormDataForPreview({
        fields: [] as FormlyFieldConfig[],
      });
      if (source !== 'editor') {
        try {
          const editorJson: any = this.editor?.getEditor?.();
          editorJson?.set?.(this.jsonEditorFormData);
        } catch (error) {
          void error;
        }
      }
      return;
    }

    this.onFormExistenceChange('YES');

    if (isSingleFieldsObject) {
      // Preserve legacy officer forms until the user explicitly migrates them
      // to transition-keyed sections. Treating this shape as empty both left the
      // selector unset and risked overwriting an existing form on the next save.
      this.jsonEditorFormData = thirdPartyForm;
      this.transitionKeys = [];
      this.selectedTransitionKey = null;
      this.formBuilderService.loadJsonFormDataForPreview({
        fields: (thirdPartyForm['fields'] as FormlyFieldConfig[]) ?? [],
      });
      if (source !== 'editor') {
        try {
          this.syncingFromEditor = true;
          const editorJson: any = this.editor?.getEditor?.();
          editorJson?.set?.(this.jsonEditorFormData);
        } catch (error) {
          void error;
        } finally {
          this.syncingFromEditor = false;
        }
      }
      return;
    } else {
      const normalized = this.normalizeTransitionMap(
        thirdPartyForm as Record<string, unknown>
      );
      this.jsonEditorFormData = normalized;
      this.transitionKeys = Object.keys(normalized);
    }

    this.selectedTransitionKey =
      this.selectedTransitionKey &&
      this.transitionKeys.includes(this.selectedTransitionKey)
        ? this.selectedTransitionKey
        : this.transitionKeys[0] ?? null;

    if (!this.selectedTransitionKey) {
      this.loadJsonEditorFormData({ fields: [] as FormlyFieldConfig[] });
      this.formBuilderService.loadJsonFormDataForPreview({
        fields: [] as FormlyFieldConfig[],
      });
      return;
    }

    const currentPayload = this.jsonEditorFormData[
      this.selectedTransitionKey
    ] as Record<string, FormlyFieldConfig[]>;

    const rawFields = ((currentPayload &&
      (currentPayload['fields'] as FormlyFieldConfig[])) ??
      []) as FormlyFieldConfig[];

    this.loadingTransition = true;
    this.formBuilderService.loadJsonFormDataForPreview({
      fields: rawFields,
    });
    setTimeout(() => {
      this.loadingTransition = false;
    }, 200);

    if (source !== 'editor') {
      try {
        this.syncingFromEditor = true;
        const editorJson: any = this.editor?.getEditor?.();
        editorJson?.set?.(this.jsonEditorFormData);
      } catch (error) {
        void error;
      } finally {
        this.syncingFromEditor = false;
      }
    }
  }

  ngOnInit() {
    this.setupFormBuilderFunctionality();
    this.setupAutoSave();
    this.setupWorkflowDropdowns();
    this.formBuilderService.reviewModeEnabled$.next(false);
    this.initialiseFromThirdPartyForm(
      (this.changeRequestThirdPartyFormData ?? DefaultEmptyFormData) as Record<
        string,
        unknown
      >,
      'external'
    );

    if (this.changeRequest?.id || this.template?.id) {
      setTimeout(() => this.formChanges$.next(true), 1000);
    }
  }

  ngAfterViewInit(): void {
    this.restoreSelectedToggleHeader();
    this.applyCodeModeWhenEditorReady();
    setTimeout(() => this.onTabSelectChanged(this.selectedToggleHeader), 0);
  }

  private applyCodeModeWhenEditorReady(): void {
    if (this.codeModeApplyTimeout) {
      clearTimeout(this.codeModeApplyTimeout);
    }

    this.codeModeApplyTimeout = setTimeout(() => {
      try {
        this.editor?.setMode('code');
      } catch (error) {
        void error;
      } finally {
        this.codeModeApplyTimeout = null;
      }
    }, 0);
  }

  private loadWorkflowForDropdowns(): void {
    const changeRequestId = this.changeRequest?.id;
    const templateId = this.template?.id;
    if (!changeRequestId && !templateId) return;

    this.workflowDropdownsLoading = true;
    const request$ = changeRequestId
      ? this.serviceChangeRequestService.getServiceChangeRequestWorkflowDefinitionAndNextStepsAndProcessingTime(
          changeRequestId
        )
      : this.serviceTemplateService.getTemplateWorkflowNextStepsProcessingTime(
          templateId!
        );

    request$
      .pipe(
        takeUntil(this.formlyFieldsSubjectControl$),
        finalize(() => (this.workflowDropdownsLoading = false))
      )
      .subscribe({
        next: res => {
          const data = res?.data as Record<string, unknown> | undefined;
          const workflow = (data?.['workflow'] as any[]) ?? [];
          this.workflowSteps = workflow
            .filter(
              (s: any) =>
                s?.startState != null &&
                s?.event != null &&
                String(s.event).trim() === 'APPROVE_WITH_FORM'
            )
            .map((s: any) => ({
              startState: String(s.startState),
              event: String(s.event).trim(),
            }));
          this.availableStartStates = [
            ...new Set(this.workflowSteps.map(s => s.startState)),
          ].sort();
          this.updateAvailableEventsForStartState(
            this.transitionForm.get('startState')?.value
          );
        },
        error: () => {
          this.workflowSteps = [];
          this.availableStartStates = [];
          this.availableEvents = [];
        },
      });
  }

  private updateAvailableEventsForStartState(startState: string | null): void {
    if (!startState) {
      this.availableEvents = [];
      return;
    }
    this.availableEvents = [
      ...new Set(
        this.workflowSteps
          .filter(s => s.startState === startState)
          .map(s => s.event)
      ),
    ].sort();
  }

  private setupWorkflowDropdowns(): void {
    this.loadWorkflowForDropdowns();
    this.transitionForm
      .get('startState')
      ?.valueChanges.pipe(takeUntil(this.formlyFieldsSubjectControl$))
      .subscribe(startState => {
        this.updateAvailableEventsForStartState(startState ?? null);
        this.transitionForm.get('event')?.setValue('', { emitEvent: false });
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['changeRequestThirdPartyFormData']?.currentValue) {
      this.changeRequestThirdPartyFormData =
        changes['changeRequestThirdPartyFormData'].currentValue;
      this.initialiseFromThirdPartyForm(
        changes['changeRequestThirdPartyFormData'].currentValue as Record<
          string,
          unknown
        >,
        'external'
      );
      this.restoreCodeEditorViewIfNeeded();
    }

    // changeRequest/template can arrive (or resolve to their real id) after
    // ngOnInit already ran loadWorkflowForDropdowns() once — e.g. the parent
    // fetches the change request asynchronously and this component isn't
    // recreated when it reassigns the input. Without re-running it here, the
    // state/event dropdowns stay stuck on whatever (possibly id-less) value
    // was present at the very first change, and only a full page reload
    // (fresh ngOnInit, by then-resolved data) would populate them.
    const changeRequestChanged =
      changes['changeRequest'] && !changes['changeRequest'].firstChange;
    const templateChanged =
      changes['template'] && !changes['template'].firstChange;
    if (changeRequestChanged || templateChanged) {
      this.loadWorkflowForDropdowns();
    }
  }

  onFormExistenceChange(value: string): void {
    this.formExistsForm?.get('formExists')?.setValue(value);
    if (value === 'YES') {
      this.applyCodeModeWhenEditorReady();
    }
  }

  onTransitionKeyChange(key: string): void {
    this.syncCurrentTransitionFieldsFromBuilder(this.selectedTransitionKey);
    this.selectedTransitionKey = key;
    const payload = this.jsonEditorFormData[key] as Record<
      string,
      FormlyFieldConfig[]
    >;
    if (!payload) return;
    const rawFields = ((payload &&
      (payload['fields'] as FormlyFieldConfig[])) ??
      []) as FormlyFieldConfig[];

    this.loadingTransition = true;
    this.formBuilderService.loadJsonFormDataForPreview({
      fields: rawFields,
    });
    setTimeout(() => {
      this.loadingTransition = false;
    }, 200);
  }

  onAddTransitionFromForm(): void {
    if (this.transitionForm.invalid) return;
    this.syncCurrentTransitionFieldsFromBuilder(this.selectedTransitionKey);
    const startState = this.transitionForm.get('startState')?.value?.trim();
    const event = this.transitionForm.get('event')?.value?.trim();
    if (!startState || !event) return;

    const key = `${startState}-${event}`;

    if (
      !this.jsonEditorFormData ||
      typeof this.jsonEditorFormData !== 'object'
    ) {
      this.jsonEditorFormData = {};
    }

    if (key !== 'OFFICER_FORM') {
      this.jsonEditorFormData = this.normalizeTransitionMap(
        this.jsonEditorFormData as Record<string, unknown>
      );
      this.transitionKeys = Object.keys(
        this.jsonEditorFormData as Record<string, unknown>
      );
    }

    if (!this.jsonEditorFormData[key]) {
      (this.jsonEditorFormData as any)[key] = {
        fields: [],
      };
      this.transitionKeys = [...new Set([...this.transitionKeys, key])];
    }

    this.selectedTransitionKey = key;

    const payload = this.jsonEditorFormData[key] as Record<
      string,
      FormlyFieldConfig[]
    >;

    const rawFields = ((payload &&
      (payload['fields'] as FormlyFieldConfig[])) ??
      []) as FormlyFieldConfig[];

    this.loadingTransition = true;
    this.formBuilderService.loadJsonFormDataForPreview({
      fields: rawFields,
    });
    setTimeout(() => {
      this.loadingTransition = false;
    }, 200);

    try {
      const editorJson: any = this.editor?.getEditor?.();
      editorJson?.set?.(this.jsonEditorFormData);
    } catch (error) {
      void error;
    }

    this.transitionForm.reset();
  }

  private normalizeTransitionMap(
    value: Record<string, unknown>
  ): Record<string, unknown> {
    const copy: Record<string, unknown> = { ...value };
    const keys = Object.keys(copy);
    if (keys.length > 1 && keys.includes('OFFICER_FORM')) {
      delete copy['OFFICER_FORM'];
    }
    return copy;
  }

  onStartAddAnotherTransition(): void {
    this.transitionForm.reset();
  }

  loadJsonEditorFormData(formlyFields: Record<string, FormlyFieldConfig[]>) {
    this.jsonEditorFormData = formlyFields;
  }

  onDeleteSelectedTransition(): void {
    if (
      !this.selectedTransitionKey ||
      !this.jsonEditorFormData ||
      typeof this.jsonEditorFormData !== 'object'
    ) {
      return;
    }

    const keyToDelete = this.selectedTransitionKey;
    delete (this.jsonEditorFormData as Record<string, unknown>)[keyToDelete];

    this.transitionKeys = this.transitionKeys.filter(k => k !== keyToDelete);
    this.selectedTransitionKey = this.transitionKeys[0] ?? null;

    try {
      this.syncingFromEditor = true;
      const editorJson: any = this.editor?.getEditor?.();
      editorJson?.set?.(this.jsonEditorFormData);
    } catch (error) {
      void error;
    } finally {
      this.syncingFromEditor = false;
    }

    if (this.selectedTransitionKey) {
      this.onTransitionKeyChange(this.selectedTransitionKey);
    } else {
      this.formBuilderService.loadJsonFormDataForPreview({
        fields: [] as FormlyFieldConfig[],
      });
    }
  }

  onRenameSelectedTransition(): void {
    if (
      !this.selectedTransitionKey ||
      !this.jsonEditorFormData ||
      typeof this.jsonEditorFormData !== 'object'
    ) {
      return;
    }

    const oldKey = this.selectedTransitionKey;
    const currentPayload = (this.jsonEditorFormData as Record<string, unknown>)[
      oldKey
    ];

    if (!currentPayload) {
      return;
    }

    const newKeyRaw = window.prompt(
      'Enter new transition name (e.g. START_EVENT-END_EVENT):',
      oldKey
    );

    if (!newKeyRaw) {
      return;
    }

    const newKey = newKeyRaw.trim();
    if (!newKey || newKey === oldKey) {
      return;
    }

    delete (this.jsonEditorFormData as Record<string, unknown>)[oldKey];
    (this.jsonEditorFormData as Record<string, unknown>)[newKey] =
      currentPayload;

    this.transitionKeys = this.transitionKeys.map(k =>
      k === oldKey ? newKey : k
    );
    this.selectedTransitionKey = newKey;

    try {
      this.syncingFromEditor = true;
      const editorJson: any = this.editor?.getEditor?.();
      editorJson?.set?.(this.jsonEditorFormData);
    } catch (error) {
      void error;
    } finally {
      this.syncingFromEditor = false;
    }

    this.onTransitionKeyChange(newKey);
  }

  async validateJsonEditor(): Promise<void> {
    const editorJson: any = this.editor?.getEditor?.();
    if (!editorJson) {
      return;
    }
    try {
      this.syncingFromEditor = true;
      this.jsonEditorValidationErrors = await editorJson.validate();
      if (this.jsonEditorValidationErrors.length > 0) return;

      const changedData = editorJson.get() as Record<string, unknown>;

      if (
        !this.jsonEditorFormData ||
        typeof this.jsonEditorFormData !== 'object'
      ) {
        this.jsonEditorFormData = {} as Record<string, unknown>;
      }

      Object.keys(this.jsonEditorFormData).forEach(key => {
        if (!(key in changedData)) {
          delete (this.jsonEditorFormData as Record<string, unknown>)[key];
        }
      });

      Object.keys(changedData).forEach(key => {
        (this.jsonEditorFormData as Record<string, unknown>)[key] =
          changedData[key];
      });
    } catch (error) {
      console.log(error);
    } finally {
      this.initialiseFromThirdPartyForm(this.jsonEditorFormData, 'editor');
      this.syncingFromEditor = false;
    }
  }

  async validateJsonEditorInputOnly(): Promise<void> {
    const editorJson: any = this.editor?.getEditor?.();
    if (!editorJson) {
      return;
    }
    try {
      this.jsonEditorValidationErrors = await editorJson.validate();
    } catch (error) {
      console.log(error);
    }
  }

  onCodeEditorChanged(): void {
    if (this.syncingFromEditor) {
      return;
    }
    if (this.codeEditorSyncTimeout) {
      clearTimeout(this.codeEditorSyncTimeout);
    }
    this.codeEditorSyncTimeout = setTimeout(async () => {
      await this.validateJsonEditor();
      if (this.jsonEditorValidationErrors.length === 0) {
        this.formChanges$.next(true);
      }
    }, 300);
  }

  private sanitizeAndReloadCodeEditor(): void {
    try {
      this.syncingFromEditor = true;
      const editorJson: any = this.editor?.getEditor?.();
      editorJson?.set?.(this.jsonEditorFormData ?? {});
    } catch (error) {
      void error;
    } finally {
      this.syncingFromEditor = false;
    }
  }

  private checkJsonFormDataIsNotEmpty(json: Record<string, unknown>): IError[] {
    const errors: IError[] = [];
    const propertiesArray: string[] = Object.keys(json);
    if (propertiesArray.includes('isTrusted')) {
      propertiesArray.splice(propertiesArray.indexOf('isTrusted'), 1);
    }

    if (propertiesArray.length < 1) {
      errors.push({
        path: [1],
        message: 'JSON Object can not be empty.',
      });
    }

    return errors;
  }

  async onThirdPartyFormSubmit() {
    if (!this.changeRequest.id && !this.template?.id) return;
    this.isLoading = true;
    if (this.autoSaveTimeout) {
      clearTimeout(this.autoSaveTimeout);
      this.autoSaveTimeout = null;
    }
    this.formChanges$.next(false);

    if (this.formExistsForm.get('formExists')?.value === 'YES') {
      this.syncCurrentTransitionFieldsFromBuilder(this.selectedTransitionKey);

      try {
        const editorJson: any = this.editor?.getEditor?.();
        editorJson?.set?.(this.jsonEditorFormData);
      } catch (error) {
        void error;
      }

      if (
        this.selectedToggleHeader ===
        EServiceChangeRequestToggleHeaders.CODE_EDITOR
      ) {
        await this.validateJsonEditor();
      }

      this.updateFormDisplayedErrorMessageContent(false);
      const formDefination = {
        thirdPartyForm: this.jsonEditorFormData as Record<string, unknown>,
      };

      if (this.isTemplateMode && this.template?.id) {
        this.serviceTemplateService
          .updateTemplateThirdPartyFormDefinition(
            this.template.id,
            formDefination
          )
          .pipe(finalize(() => (this.isLoading = false)))
          .subscribe({
            next: (res: IHttpSingleDataResponse<Record<string, unknown>>) => {
              this.toastService.show({
                body: 'Template officer form updated successfully',
                type: 'success',
              });
              this.officerThirdPartyFormPersisted.emit(
                formDefination.thirdPartyForm
              );
              this.officerFormSubmitted.emit(true);
            },
            error: (error: HttpErrorResponse) => {
              const errorMessageAndCode: IHttpErrorResponseCodeAndMessage =
                ExtractHttpErrorResponseCodeAndMessage(
                  error,
                  `Failed to update template third party form properties`
                );
              this.toastService.show({
                body: errorMessageAndCode.message,
                type: 'error',
              });
              const fieldErrors: IFieldError[] =
                ExtractHttpErrorFieldErrorsUtil(error);
              this.updateFormDisplayedErrorMessageContent(
                true,
                'danger',
                'Template Form Definition Not Updated',
                errorMessageAndCode.message,
                fieldErrors
              );
              this.officerFormSubmitted.emit(false);
            },
          });
      } else {
        this.serviceChangeRequestService
          .updateServiceChangeRequestThirdPartyFormDefinition(
            this.changeRequest.id!,
            formDefination
          )
          .pipe(finalize(() => (this.isLoading = false)))
          .subscribe({
            next: (res: IHttpSingleDataResponse<Record<string, unknown>>) => {
              this.toastService.show({
                body: res.responseMessage,
                type: 'success',
              });
              this.officerThirdPartyFormPersisted.emit(
                formDefination.thirdPartyForm
              );
              this.officerFormSubmitted.emit(true);
            },
            error: (error: HttpErrorResponse) => {
              const errorMessageAndCode: IHttpErrorResponseCodeAndMessage =
                ExtractHttpErrorResponseCodeAndMessage(
                  error,
                  `Failed to update Change request third party form properties`
                );
              this.toastService.show({
                body: errorMessageAndCode.message,
                type: 'error',
              });
              const fieldErrors: IFieldError[] =
                ExtractHttpErrorFieldErrorsUtil(error);
              this.updateFormDisplayedErrorMessageContent(
                true,
                'danger',
                'Form Definition Not Updated',
                errorMessageAndCode.message,
                fieldErrors
              );
              this.officerFormSubmitted.emit(false);
            },
          });
      }
    } else {
      if (this.isTemplateMode && this.template?.id) {
        this.serviceTemplateService
          .updateTemplateThirdPartyFormDefinition(this.template.id, {
            thirdPartyForm: {},
          })
          .pipe(finalize(() => (this.isLoading = false)))
          .subscribe({
            next: (res: IHttpSingleDataResponse<Record<string, unknown>>) => {
              this.toastService.show({
                body: 'Template officer form removed successfully',
                type: 'success',
              });
              this.officerThirdPartyFormPersisted.emit({});
              this.officerFormSubmitted.emit(true);
            },
            error: (error: HttpErrorResponse) => {
              const errorMessageAndCode: IHttpErrorResponseCodeAndMessage =
                ExtractHttpErrorResponseCodeAndMessage(
                  error,
                  `Failed to update template third party form properties`
                );
              this.toastService.show({
                body: errorMessageAndCode.message,
                type: 'error',
              });
              const fieldErrors: IFieldError[] =
                ExtractHttpErrorFieldErrorsUtil(error);
              this.updateFormDisplayedErrorMessageContent(
                true,
                'danger',
                'Template Form Definition Not Updated',
                errorMessageAndCode.message,
                fieldErrors
              );
              this.officerFormSubmitted.emit(false);
            },
          });
      } else {
        this.serviceChangeRequestService
          .removeServiceChangeRequestThirdPartyFormDefinition(
            this.changeRequest.id!
          )
          .pipe(finalize(() => (this.isLoading = false)))
          .subscribe({
            next: (res: IHttpSingleDataResponse<Record<string, unknown>>) => {
              this.toastService.show({
                body: res.responseMessage,
                type: 'success',
              });
              this.officerThirdPartyFormPersisted.emit({});
              this.officerFormSubmitted.emit(true);
            },
            error: (error: HttpErrorResponse) => {
              const errorMessageAndCode: IHttpErrorResponseCodeAndMessage =
                ExtractHttpErrorResponseCodeAndMessage(
                  error,
                  `Failed to update Change request third party form properties`
                );
              this.toastService.show({
                body: errorMessageAndCode.message,
                type: 'error',
              });
              const fieldErrors: IFieldError[] =
                ExtractHttpErrorFieldErrorsUtil(error);
              this.updateFormDisplayedErrorMessageContent(
                true,
                'danger',
                'Form Definition Not Updated',
                errorMessageAndCode.message,
                fieldErrors
              );
              this.officerFormSubmitted.emit(false);
            },
          });
      }
    }
  }

  private syncCurrentTransitionFieldsFromBuilder(
    transitionKey: string | null = this.selectedTransitionKey
  ): void {
    if (!transitionKey) return;
    const current = this.formBuilderService.formlyFieldsSubject$.getValue();
    const rawFields = (current?.fields ?? []) as FormlyFieldConfig[];
    const convertedFields =
      (FormlyFieldArrayToIremboFormlyFieldsConverter(
        rawFields as any
      ) as FormlyFieldConfig[]) ?? [];
    const safeFields = this.ensureContainerFieldGroups(convertedFields);

    if (
      !this.jsonEditorFormData ||
      typeof this.jsonEditorFormData !== 'object'
    ) {
      this.jsonEditorFormData = {};
    }

    const existingPayload =
      ((this.jsonEditorFormData as Record<string, unknown>)[
        transitionKey
      ] as Record<string, unknown>) ?? {};

    (this.jsonEditorFormData as Record<string, unknown>)[transitionKey] = {
      ...existingPayload,
      fields: safeFields,
    };
  }

  onTabSelectChanged(event: EServiceChangeRequestToggleHeaders) {
    this.selectedToggleHeader = event;
    this.persistSelectedToggleHeader();
    if (event === EServiceChangeRequestToggleHeaders.CODE_EDITOR) {
      this.sanitizeAndReloadCodeEditor();
      this.applyCodeModeWhenEditorReady();
      setTimeout(() => this.syncTransitionListFromEditor(), 100);
    }
    if (event !== EServiceChangeRequestToggleHeaders.WIZARD) {
      this.formBuilderService.reviewModeEnabled$.next(true);
    } else {
      this.formBuilderService.reviewModeEnabled$.next(false);
    }
    this.formBuilderService.refreshFormlyForm();
  }

  private restoreCodeEditorViewIfNeeded(): void {
    if (
      this.selectedToggleHeader !==
      EServiceChangeRequestToggleHeaders.CODE_EDITOR
    ) {
      return;
    }
    setTimeout(() => {
      try {
        this.sanitizeAndReloadCodeEditor();
        this.applyCodeModeWhenEditorReady();
        this.syncTransitionListFromEditor();
      } catch (error) {
        void error;
      }
    }, 0);
  }

  private getToggleHeaderStorageKey(): string {
    const id = this.changeRequest?.id || this.template?.id || 'unknown';
    return `officerForm.selectedToggleHeader.${id}`;
  }

  private persistSelectedToggleHeader(): void {
    try {
      window.sessionStorage?.setItem(
        this.getToggleHeaderStorageKey(),
        String(this.selectedToggleHeader)
      );
    } catch (error) {
      void error;
    }
  }

  private restoreSelectedToggleHeader(): void {
    try {
      const stored = window.sessionStorage?.getItem(
        this.getToggleHeaderStorageKey()
      ) as EServiceChangeRequestToggleHeaders | null;
      if (
        stored === EServiceChangeRequestToggleHeaders.WIZARD ||
        stored === EServiceChangeRequestToggleHeaders.CODE_EDITOR ||
        stored === EServiceChangeRequestToggleHeaders.REVIEW
      ) {
        this.selectedToggleHeader = stored;
      }
    } catch (error) {
      void error;
    }
  }

  private syncTransitionListFromEditor(): void {
    try {
      const editorJson: any = this.editor?.getEditor?.();
      const data = editorJson?.get?.() as Record<string, unknown> | undefined;
      if (!data || typeof data !== 'object') return;
      const keys = Object.keys(data);
      if (keys.length === 0) return;
      const isSingleFields =
        keys.length === 1 &&
        keys[0] === 'fields' &&
        Array.isArray((data as any).fields);
      if (isSingleFields) return;
      this.transitionKeys = keys;
      if (
        !this.selectedTransitionKey ||
        !this.transitionKeys.includes(this.selectedTransitionKey)
      ) {
        this.selectedTransitionKey = this.transitionKeys[0] ?? null;
        if (this.selectedTransitionKey) {
          this.onTransitionKeyChange(this.selectedTransitionKey);
        }
      }
    } catch (error) {
      void error;
    }
  }

  private setupFormBuilderFunctionality(): void {
    this.formBuilderService.formlyFieldsSubject$
      .pipe(takeUntil(this.formlyFieldsSubjectControl$))
      .subscribe((formBuilderFieldsData: IFormBuilderFieldsData) => {
        if (
          this.selectedTransitionKey &&
          !this.syncingFromEditor &&
          !this.loadingTransition
        ) {
          const rawFields = formBuilderFieldsData.fields as FormlyFieldConfig[];
          const convertedFields =
            (FormlyFieldArrayToIremboFormlyFieldsConverter(
              rawFields as any
            ) as FormlyFieldConfig[]) ?? [];
          const fields = this.ensureContainerFieldGroups(convertedFields);

          if (
            !this.jsonEditorFormData ||
            typeof this.jsonEditorFormData !== 'object'
          ) {
            this.jsonEditorFormData = {};
          }

          const existingPayload =
            ((this.jsonEditorFormData as Record<string, unknown>)[
              this.selectedTransitionKey
            ] as Record<string, unknown>) || {};

          (this.jsonEditorFormData as Record<string, unknown>)[
            this.selectedTransitionKey
          ] = {
            ...existingPayload,
            fields,
          };

          try {
            const editorJson: any = this.editor?.getEditor?.();
            editorJson?.set?.(this.jsonEditorFormData);
          } catch (error) {
            void error;
          }
        }

        if (!formBuilderFieldsData.forceReload) {
          setTimeout(() => this.formChanges$.next(true), 500);
        }
      });
  }

  private ensureContainerFieldGroups(
    fields: FormlyFieldConfig[]
  ): FormlyFieldConfig[] {
    const containerTypes = new Set(['sections', 'block', 'formly-group']);
    return (fields ?? []).map(field => {
      const next: FormlyFieldConfig = { ...field };
      if (Array.isArray(next.fieldGroup)) {
        next.fieldGroup = this.ensureContainerFieldGroups(
          next.fieldGroup as FormlyFieldConfig[]
        );
      } else if (containerTypes.has(String(next.type))) {
        next.fieldGroup = [];
      }
      return next;
    });
  }

  private setupAutoSave(): void {
    this.formChanges$
      .pipe(
        debounceTime(3000),
        distinctUntilChanged(),
        takeUntil(this.formlyFieldsSubjectControl$)
      )
      .subscribe(hasChanges => {
        if (
          hasChanges &&
          !this.autoSaveInProgress &&
          (this.changeRequest?.id || this.template?.id)
        ) {
          if (
            this.jsonEditorFormData &&
            Object.keys(this.jsonEditorFormData).length > 0
          ) {
            if (this.jsonEditorValidationErrors.length === 0) {
              this.autoSave();
            }
          }
        }
      });
  }

  private autoSave(): void {
    if (!this.changeRequest?.id && !this.template?.id) return;

    this.autoSaveInProgress = true;
    const formDefination = {
      thirdPartyForm: this.jsonEditorFormData as Record<string, unknown>,
    };

    const updateObservable =
      this.isTemplateMode && this.template?.id
        ? this.serviceTemplateService.updateTemplateThirdPartyFormDefinition(
            this.template.id,
            formDefination
          )
        : this.serviceChangeRequestService.updateServiceChangeRequestThirdPartyFormDefinition(
            this.changeRequest.id!,
            formDefination
          );

    updateObservable
      .pipe(
        finalize(() => {
          this.autoSaveInProgress = false;
        })
      )
      .subscribe({
        next: () => {
          this.lastAutoSaved = new Date();
          this.formChanges$.next(false);
        },
        error: () => {
          if (this.autoSaveTimeout) {
            clearTimeout(this.autoSaveTimeout);
          }
          this.autoSaveTimeout = setTimeout(() => {
            this.formChanges$.next(true);
          }, 10000);
        },
      });
  }

  updateFormDisplayedErrorMessageContent(
    show: boolean,
    type: TUiAlertTypes = 'warning',
    title = '',
    message = '',
    fieldErrors: IFieldError[] | null = null
  ): void {
    let extraMessage = '';
    this.showConfigFormErrorMessage = show;

    if (fieldErrors) {
      extraMessage = fieldErrors
        .map((field: IFieldError) => `${field.field}: ${field?.errorCode}`)
        .join(`<br/>\n`);
    }

    this.formAlertContent = { type, title, message, extraMessage };

    if (show) {
      this.errorMessageElement.nativeElement.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }

  ngOnDestroy(): void {
    this.formlyFieldsSubjectControl$.next();
    this.formlyFieldsSubjectControl$.complete();
    this.formBuilderService.configureFieldAction$.next(null);

    if (this.autoSaveTimeout) {
      clearTimeout(this.autoSaveTimeout);
    }
    if (this.codeEditorSyncTimeout) {
      clearTimeout(this.codeEditorSyncTimeout);
    }
    if (this.codeModeApplyTimeout) {
      clearTimeout(this.codeModeApplyTimeout);
    }
  }
}
